/**
 * EoC-scoped next appointment lookup + Zoom sidebar confirm/reschedule/schedule UI.
 *
 * Details render inline (navigating away would drop the provider out of the live
 * call); scheduling and rescheduling open the shared session scheduler so the
 * in-call flow matches the provider calendar exactly.
 */

import { Badge, Button, Card, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { MedplumClient } from '@medplum/core';
import type { Appointment, Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EPISODE_OF_CARE_CONTEXT_EXT } from '@unify/fhir-model/constants';
import { appointmentToCalendarEvent } from '@/features/calendar/appointmentToCalendarEvent';
import { syncProviderAppointmentScheduleChange } from '@/features/calendar/providerAppointmentScheduleSync';
import { SessionSchedulerModal } from '@/features/calendar/SessionSchedulerModal';
import { formatUsDateTime } from '@/lib/dateHelpers';
import { buildPatientAppointmentSubscriptionCriteria } from '@/lib/fhirRealtimeCriteria';
import { useMedplumSubscriptionRefresh } from '@/hooks/useMedplumSubscriptionRefresh';
import { useCalendarPracticeId } from '@/pages/calendar/appointmentTypeCatalog';
import { zoomOverlayModalProps } from '../lib/zoomSidebarComboboxProps';

function episodeIdFromAppointment(appt: Appointment): string | undefined {
  const ref = appt.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXT)?.valueReference
    ?.reference;
  if (!ref?.startsWith('EpisodeOfCare/')) return undefined;
  return ref.slice('EpisodeOfCare/'.length);
}

export async function findNextAppointmentForEpisode(
  medplum: MedplumClient,
  patientId: string,
  episodeOfCareId: string,
  currentAppointmentId: string,
  currentStart: string | undefined,
): Promise<Appointment | undefined> {
  if (!currentStart) return undefined;
  medplum.invalidateSearches('Appointment');
  const results = await medplum.searchResources('Appointment', {
    patient: `Patient/${patientId}`,
    date: `gt${currentStart}`,
    _count: '20',
    _sort: 'date',
  });
  return results.find((a) => {
    if (a.id === currentAppointmentId) return false;
    if (a.status === 'cancelled' || a.status === 'entered-in-error') return false;
    return episodeIdFromAppointment(a) === episodeOfCareId;
  });
}

export interface NextAppointmentSidebarSectionProps {
  patientId?: string;
  patientDisplay?: string;
  episodeId?: string;
  appointmentId?: string;
  appointmentStart?: string;
}

function isPractitioner(profile: unknown): profile is Practitioner {
  return Boolean(
    profile &&
      typeof profile === 'object' &&
      (profile as { resourceType?: string }).resourceType === 'Practitioner' &&
      (profile as { id?: string }).id,
  );
}

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  proposed: { label: 'Proposed', color: 'yellow' },
  pending: { label: 'Pending', color: 'yellow' },
  booked: { label: 'Confirmed', color: 'green' },
  arrived: { label: 'Arrived', color: 'blue' },
  checkedin: { label: 'Checked in', color: 'blue' },
  fulfilled: { label: 'Completed', color: 'gray' },
  waitlist: { label: 'Waitlist', color: 'gray' },
};

function durationMinutesOf(appt: Appointment): number | undefined {
  if (appt.minutesDuration) return appt.minutesDuration;
  if (!appt.start || !appt.end) return undefined;
  const ms = new Date(appt.end).getTime() - new Date(appt.start).getTime();
  return Number.isFinite(ms) ? Math.round(ms / 60_000) : undefined;
}

/** A week after the current session, at the same time — the usual cadence. */
function defaultNextStart(currentStart: string | undefined): Date | undefined {
  if (!currentStart) return undefined;
  const start = new Date(currentStart);
  if (Number.isNaN(start.getTime())) return undefined;
  return new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
}

export function NextAppointmentSidebarSection({
  patientId,
  patientDisplay,
  episodeId,
  appointmentId,
  appointmentStart,
}: NextAppointmentSidebarSectionProps): JSX.Element | null {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const practitioner = isPractitioner(profile) ? profile : undefined;
  const organizationId = useCalendarPracticeId(practitioner);

  const [nextAppt, setNextAppt] = useState<Appointment | null | undefined>(undefined);
  const [schedulerMode, setSchedulerMode] = useState<'closed' | 'create' | 'reschedule'>('closed');
  const [confirming, setConfirming] = useState(false);

  const reload = useCallback(async () => {
    if (!patientId || !episodeId || !appointmentId) {
      setNextAppt(null);
      return;
    }
    try {
      const next = await findNextAppointmentForEpisode(
        medplum,
        patientId,
        episodeId,
        appointmentId,
        appointmentStart,
      );
      setNextAppt(next ?? null);
    } catch (err) {
      console.warn('Failed to load next EoC appointment', err);
      setNextAppt(null);
    }
  }, [appointmentId, appointmentStart, episodeId, medplum, patientId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const subscriptionCriteria = useMemo(
    () => (patientId ? buildPatientAppointmentSubscriptionCriteria(patientId) : undefined),
    [patientId],
  );
  useMedplumSubscriptionRefresh(subscriptionCriteria, () => void reload(), {
    debugLabel: '[NextAppointment][WS]',
  });

  const calendarEvent = useMemo(
    () => (nextAppt ? appointmentToCalendarEvent(nextAppt) : null),
    [nextAppt],
  );

  /** Resolved each render: the portal element only exists once the Zoom page has mounted. */
  const overlay = zoomOverlayModalProps();

  if (!patientId || !episodeId || !appointmentId) {
    return null;
  }

  const handleConfirm = async (): Promise<void> => {
    if (!nextAppt?.id) return;
    setConfirming(true);
    try {
      const before = await medplum.readResource('Appointment', nextAppt.id);
      const after = await medplum.updateResource({ ...before, status: 'booked' });
      await syncProviderAppointmentScheduleChange(medplum, {
        before,
        after,
        calendarStatus: 'confirmed',
      });
      notifications.show({ color: 'green', message: 'Next appointment confirmed' });
      setNextAppt(after);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Could not confirm',
        message: err instanceof Error ? err.message : 'Confirmation failed',
      });
    } finally {
      setConfirming(false);
    }
  };

  const status = nextAppt?.status ? STATUS_LABEL[nextAppt.status] : undefined;
  const duration = nextAppt ? durationMinutesOf(nextAppt) : undefined;
  const needsConfirmation = nextAppt?.status === 'proposed' || nextAppt?.status === 'pending';

  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>
        Next appointment
      </Text>

      {nextAppt === undefined ? (
        <Text size="xs" c="dimmed">
          Loading…
        </Text>
      ) : nextAppt ? (
        <Card withBorder padding="sm" radius="sm">
          <Stack gap="xs">
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <Text size="sm" fw={600}>
                {nextAppt.start ? formatUsDateTime(nextAppt.start) : 'Scheduled'}
              </Text>
              {status ? (
                <Badge size="xs" variant="light" color={status.color}>
                  {status.label}
                </Badge>
              ) : null}
            </Group>
            <Text size="xs" c="dimmed">
              {[
                duration ? `${duration} min` : undefined,
                calendarEvent?.modality === 'telehealth' ? 'Telehealth' : calendarEvent?.modality,
                calendarEvent?.locationLabel,
                calendarEvent?.provider,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>

            <Group gap="xs">
              {needsConfirmation ? (
                <Button size="xs" loading={confirming} onClick={() => void handleConfirm()}>
                  Confirm
                </Button>
              ) : null}
              <Button
                size="xs"
                variant="light"
                onClick={() => setSchedulerMode('reschedule')}
                disabled={!practitioner?.id}
              >
                Reschedule
              </Button>
            </Group>
          </Stack>
        </Card>
      ) : (
        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            No upcoming appointment on this episode of care.
          </Text>
          <Button
            size="xs"
            variant="light"
            onClick={() => setSchedulerMode('create')}
            disabled={!practitioner?.id}
          >
            Schedule next
          </Button>
        </Stack>
      )}

      {practitioner?.id && schedulerMode !== 'closed' ? (
        <SessionSchedulerModal
          opened
          onClose={() => setSchedulerMode('closed')}
          appointment={schedulerMode === 'reschedule' ? (nextAppt ?? undefined) : undefined}
          organizationId={organizationId}
          viewerPractitionerId={practitioner.id}
          episodeOfCareId={episodeId}
          patientId={patientId}
          patientDisplay={patientDisplay}
          defaultStart={defaultNextStart(appointmentStart)}
          onSaved={(saved) => {
            setNextAppt(saved);
            void reload();
          }}
          overlay={overlay}
        />
      ) : null}
    </Stack>
  );
}
